import React, { useMemo } from "react";
import { StyleSheet, Text as RNText } from "react-native";
import Spacer from "@components/atoms/Spacer";
import { NumberField, ErrorText } from "@components/molecules/TextInput";
import { View, Text, Card } from "@components/atoms/Themed";
import Colors from "@constants/Colors";
import Fonts from "@constants/Fonts";
import CustomIcon from "@components/atoms/Icon";
import { graphql, useFragment } from "react-relay/hooks";
import { ApplyReward$key } from "@generated/ApplyReward.graphql";
import numberFormat from "@lib/util/numberFormat";
import { calculateInfluencerReward } from "@lib/util/influencerReward";
import Avatar from "@components/atoms/Avatar";
import { ApplyPrice, ProposalPrice, KeyboardId } from "@constants/App";
import ReactForm, { useWatch } from "react-hook-form";
import { InputData } from "@components/organisms/Apply/type";
import { useTheme } from "@react-navigation/native";

const applyReward = graphql`
  fragment ApplyReward on Campaign {
    mediaType
    advertiserName
    askingPrice
    prType
  }
`;

export default function ApplyReward({
  campaignFragment,
  control,
  error,
}: {
  campaignFragment: ApplyReward$key;
  control: ReactForm.Control<InputData>;
  error: string | undefined;
}) {
  const campaign = useFragment(applyReward, campaignFragment);
  const { dark } = useTheme();

  // watchでcustomPriceの値を監視
  const watchedCustomPrice = useWatch({
    control,
    name: "customPrice",
    defaultValue: 0,
  });

  // 報酬額を計算
  const influencerReward = useMemo(() => {
    const price =
      Number(watchedCustomPrice) === 0 ||
      Number.isNaN(Number(watchedCustomPrice))
        ? 0
        : Number(watchedCustomPrice);
    return calculateInfluencerReward(price);
  }, [watchedCustomPrice]);

  return (
    <Card style={styles.container}>
      <View style={styles.head}>
        <CustomIcon color={Colors.black} name="money" size={18} />
        <Spacer width={7} />
        <Text style={styles.title}>{ApplyPrice}</Text>
      </View>
      {campaign.askingPrice != null && campaign.prType === "rewarded" && (
        <>
          <Spacer height={24} />
          <View style={styles.info}>
            <View style={styles.advertiserWrap}>
              <Avatar uri={null} />
              <Spacer width={8} />
              <View style={styles.advertiserNameTail}>
                <Text
                  ellipsizeMode="tail"
                  numberOfLines={1}
                  style={styles.advertiser}
                >
                  {campaign.advertiserName}
                </Text>
              </View>
            </View>
            <View style={styles.askingPriceWrap}>
              <Text style={styles.askingPriceLabel}>{ProposalPrice}</Text>
              <Spacer width={12} />
              <Text style={styles.askingPrice}>
                ¥{numberFormat(campaign.askingPrice)}
              </Text>
            </View>
          </View>
          <Spacer height={8} />
          <Text style={[Fonts.sr100]}>
            {ProposalPrice}を参考に{ApplyPrice}を決めてください
          </Text>
        </>
      )}

      <Spacer height={24} />

      <View style={styles.form}>
        <View>
          <Text style={styles.yen}>¥</Text>
        </View>
        <Spacer width={8} />
        <View style={styles.unitPriceInputWrap}>
          <NumberField
            control={control}
            inputAccessoryViewID={KeyboardId}
            name="customPrice"
            style={styles.unitPriceInput}
            testID="campaign-apply-price"
          />
          {error !== undefined && <ErrorText error={error} />}
        </View>
      </View>

      {/* 報酬額表示セクション */}
      {campaign.prType === "rewarded" && influencerReward > 0 && (
        <View
          style={[
            styles.rewardSection,
            {
              backgroundColor: dark
                ? "rgba(255, 255, 255, 0.1)"
                : Colors.gray10,
            },
          ]}
        >
          <View style={styles.rewardRow}>
            <RNText
              style={[
                styles.rewardLabel,
                { color: dark ? Colors.white : Colors.black },
              ]}
            >
              報酬額
            </RNText>
            <RNText
              style={[
                styles.rewardValue,
                { color: dark ? Colors.white : Colors.orange },
              ]}
            >
              ¥{numberFormat(influencerReward)}
            </RNText>
          </View>
        </View>
      )}

      <View style={styles.caution}>
        <Text>
          {"【※必読※  報酬額に関してのお知らせ】\n " +
            "本サービスのご利用にあたり、提示されている報酬金額より源泉徴収税＋30％をシステム手数料として差し引かせていただきます。何卒ご理解のほどよろしくお願い申し上げます。"}
        </Text>
      </View>
    </Card>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  head: {
    flexDirection: "row",
    alignItems: "center",
  },
  title: {
    ...Fonts.xlb160,
    color: Colors.black,
  },
  info: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  advertiserWrap: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
  },
  advertiserNameTail: {
    flex: 1,
  },
  advertiser: {
    ...Fonts.mr100,
  },
  askingPriceWrap: {
    flexDirection: "row",
    alignItems: "center",
  },
  askingPriceLabel: {
    ...Fonts.lb100,
  },
  askingPrice: {
    ...Fonts.ensb100,
    color: Colors.orange,
  },
  form: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  yen: {
    color: Colors.black,
    ...Fonts.enmb100,
  },
  unitPriceInputWrap: {
    flex: 1,
  },
  unitPriceInput: {
    height: 56,
    ...Fonts.enlb100,
    lineHeight: 46,
    paddingRight: 12,
    textAlign: "right",
    alignItems: "center",
    justifyContent: "center",
  },
  caution: {
    paddingTop: 8,
    ...Fonts.sr100,
  },
  // 報酬額表示用スタイル
  rewardSection: {
    borderRadius: 8,
    padding: 16,
    marginTop: 16,
    marginBottom: 8,
  },
  rewardRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  rewardLabel: {
    ...Fonts.xlb100,
    color: Colors.black,
  },
  rewardValue: {
    ...Fonts.ensb100,
    color: Colors.orange,
    fontSize: 20,
  },
});
